/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect, useState } from 'react';
import {
	Box,
	Progress,
	Image,
	rem,
	Text,
} from '@mantine/core';
import classes from './SingleFileUploadWithProgress.module.css';

// Icons
import SheetIcon from '@assets/images/sheet-file-icon.svg';
import CancelUploadIcon from '@assets/images/cancel-upload-icon.svg';
import {
	IconCircleCheckFilled,
	IconTrash,
} from '@tabler/icons-react';

// Utils
import { truncateString } from '@utils/functions';
import { Tooltip } from '@components/atoms/Tooltip';
import { useUploadMedia } from 'shared/api/upload-media';

export function uploadFile(
	onProgress: (percentage: number) => void
) {
	onProgress(Math.round(100));
}

export interface SingleFileUploadWithProgressProps {
	entity?: string;
	isPublicFile?: boolean;
	file: any;
	onDelete: (file: File) => void;
	onUpload: (file: File, status: any) => void;
	onReload: () => void;
	onGetStatus: (status: any) => void;
	isToUpdate: boolean;
	id?: string;
	documentId?: string;
	route?: string;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const SingleFileUploadWithProgress = ({
	entity,
	isPublicFile,
	file,
	onDelete,
	onUpload,
	onReload,
	onGetStatus,
	isToUpdate,
	documentId,
	route,
}: SingleFileUploadWithProgressProps) => {
	const [progress, setProgress] = useState(0);

	const uploadTechnicalDocuments = useUploadMedia();

	useEffect(() => {
		async function upload() {
			const formData = new FormData();
			if (!isPublicFile && documentId && route) {
				formData.append('document', documentId);
				formData.append('route', route);
			}
			if (!isToUpdate) {
				uploadTechnicalDocuments.mutate(formData);
			}
			formData.append('file', file);
			await uploadFile(setProgress);

			onUpload(file, uploadTechnicalDocuments.isSuccess);
		}

		upload();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (uploadTechnicalDocuments) {
			onGetStatus({
				[file.path]: uploadTechnicalDocuments,
			});
		}
	}, [uploadTechnicalDocuments.isLoading]);

	return (
		<Box className={classes.container}>
			<Box className={classes.wrapper}>
				<Image
					style={{
						width: rem(29),
						height: rem(29),
						color: 'var(--mantine-color-dimmed)',
					}}
					src={SheetIcon}
				/>
				<Tooltip
					isActive={
						file && file.path && file.path.length > 20
					}
					label={file && file.path && file.path}
				>
					<Text className={classes.filename}>
						{truncateString(
							file && file.path && file.path,
							20
						)}
					</Text>
				</Tooltip>
				<Box className={classes.containerProgress}>
					<Progress.Root
						size="md"
						className={classes.progressRoot}
					>
						<Progress.Section
							color={
								!uploadTechnicalDocuments.isLoading
									? 'success'
									: 'orange'
							}
							value={progress}
							style={{
								borderRadius: '1px',
							}}
						/>
					</Progress.Root>
					{progress == 100 &&
						!uploadTechnicalDocuments.isLoading && (
							<IconCircleCheckFilled
								style={{
									width: rem(30),
									height: rem(30),
									color: 'var(--mantine-color-success-9)',
								}}
								stroke={1.5}
							/>
						)}
					{progress !== 100 && (
						<Image
							data-testid="D2LtDXR7_Z5jdHi11-button"
							onClick={() => {
								onDelete(file);
							}}
							className={classes.cancelUpload}
							src={CancelUploadIcon}
						/>
					)}
					{progress == 100 &&
						!uploadTechnicalDocuments.isLoading && (
							<IconTrash
								className={classes.reloadRowIcon}
								data-testid="D2EtDXR7_Z5jdHi6B-dropzone"
								onClick={() => {
									onReload();
								}}
							/>
						)}
				</Box>
			</Box>
		</Box>
	);
};

export default SingleFileUploadWithProgress;
